import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ProductCard } from "../components/ProductCard";
import { api } from "../lib/api";
import { useContent } from "../context/ContentContext";

export default function Archive() {
  const { p } = useContent();
  const h = p("archive").header || {};
  const fc = p("archive").footer_cta || {};
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products/browse", { params: { kind: "etching", sold: true, sort: "newest", limit: 200 } })
      .then((r) => setProducts(r.data.items || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="archive-page">
      <Seo title="The Archive — Sold Works — CaminoSapiens"
        description="An archive of original photogravure etchings that have found their homes. Each began as a single photograph — and yours could become the next." />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Original Etchings", to: "/etchings" }, { label: "The Archive" }]} />

      <div className="max-w-2xl mb-14">
        <p className="overline mb-4">{h.eyebrow}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">{h.heading}</h1>
        <p className="text-[#4A4947] mt-6 leading-relaxed">{h.body}</p>
        <Link to="/custom-etching" data-testid="archive-commission-cta"
          className="inline-flex items-center gap-2 mt-8 bg-[#1C1B1A] text-[#F9F8F6] px-8 py-4 text-sm hover:bg-[#B35D4B] transition-colors">
          {h.cta_label} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {[0, 1, 2].map((i) => <div key={i} className="animate-pulse h-[380px] bg-[#F0EFEA] border border-[#E4E1DB]" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="border-y border-[#E4E1DB] py-16 text-center" data-testid="archive-empty">
          <p className="text-[#4A4947]">{h.empty_text}</p>
          <Link to="/etchings" className="inline-flex items-center gap-2 mt-6 text-sm link-underline">Browse the etchings <ArrowRight className="w-4 h-4" /></Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14" data-testid="archive-grid">
          {products.map((prod, i) => <ProductCard key={prod.id} p={prod} index={i} />)}
        </div>
      )}

      {/* Closing nudge toward a commission */}
      <div className="mt-24 border border-[#E4E1DB] bg-[#F0EFEA] px-6 sm:px-12 py-14 text-center" data-testid="archive-footer-cta">
        <p className="overline mb-4">{fc.eyebrow}</p>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight max-w-xl mx-auto">{fc.heading}</h2>
        <p className="text-[#4A4947] mt-5 max-w-lg mx-auto leading-relaxed">{fc.body}</p>
        <Link to="/custom-etching"
          className="inline-flex items-center gap-2 mt-8 bg-[#1C1B1A] text-[#F9F8F6] px-8 py-4 text-sm hover:bg-[#B35D4B] transition-colors">
          {fc.cta_label} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="mt-16 text-center">
        <Link to="/etchings" className="inline-flex items-center gap-2 text-sm link-underline">View available etchings <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );
}
